import { useState, useEffect, useCallback } from 'react';
import { requestCountryData } from '../services/requestCommunityNews';

/**
 * Hook for fetching and managing country data
 * @param {string} initialCountryCode - Optional initial three-letter country code (e.g., "UGA")
 * @returns {object} - Country data state and fetch function
 */
export const useCountryData = (initialCountryCode = null) => {
  const [countryData, setCountryData] = useState(null);
  const [status, setStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [error, setError] = useState(null);

  /**
   * Fetches country data by country code
   * @param {string} countryCode - Three-letter country code (e.g., "UGA")
   * @returns {Promise<object|null>} - Country data or null if error
   */
  const fetchCountryData = useCallback(async (countryCode) => {
    if (!countryCode) {
      console.warn("No country code provided to fetchCountryData");
      return null;
    }

    try {
      setStatus('loading');
      setError(null);

      const data = await requestCountryData(countryCode);

      setCountryData(data);
      setStatus('success');

      return data;
    } catch (error) {
      console.error("Error fetching country data:", error);
      setStatus('error');
      setError(error);
      return null;
    }
  }, []);

  // Fetch data if initialCountryCode is provided
  useEffect(() => {
    if (initialCountryCode) {
      fetchCountryData(initialCountryCode);
    }
  }, [initialCountryCode, fetchCountryData]);

  return {
    countryData,
    status,
    error,
    isLoading: status === 'loading',
    isError: status === 'error',
    isSuccess: status === 'success',
    fetchCountryData
  };
};

export default useCountryData;
